class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleRemoveButtonClick.bind(this));
  }

  async handleRemoveButtonClick(event) {
    event.preventDefault();

    // Find the closest cart items element
    const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');

    // Check if the button is part of a bundle item or has gift wrapping
    if (this.getAttribute('data-is-bundle') === 'true' || this.getAttribute('data-has-gift-wrapping')) {
      cartItems.updateQuantities(this.dataset.index, 0, 'minus', this);
    } else {
      // If it is a regular item, update its quantity to zero
      cartItems.updateQuantity(this.dataset.index, 0, 'minus');
    }
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)));
    this.input.addEventListener('input', this.onInputChange.bind(this));
    this.input.addEventListener('blur', this.onBlur.bind(this));
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }

  onInputChange(event) {
    const value = event.target.value;
    if (!/^\d*$/.test(value)) {
      event.target.value = value.replace(/[^\d]/g, '');
    }
  }

  onBlur(event) {
    const value = event.target.value;
    if (!value || Number(value) <= 0) {
      event.target.value = 1;
      event.target.dispatchEvent(this.changeEvent);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);

    this.addEventListener('change', this.debouncedOnChange.bind(this));

    document.addEventListener('ajaxProduct:added', ({ detail }) => {
      this.renderAndShowItems();
    });

    document.addEventListener('cart:updateDrawer', () => {
      this.renderAndShowItems(true);
    });

    document.addEventListener('ajaxProduct:cleared', () => {
      this.renderAndShowItems();
    });
  }

  onChange(event) {
    if (event.target.getAttribute('data-is-bundle') === 'true' || event.target.getAttribute('data-has-gift-wrapping')) {
      this.updateQuantities(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'), event.target);

      return;
    }

    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  // Shopify limit: only 5 sections can be rendered (https://shopify.dev/api/ajax/reference/cart#bundled-section-rendering)
  getSectionsToRender() {
    return [
      {
        id: document.querySelector('.main-cart').dataset.id,
        selector: '#cart-items',
      },
      {
        id: document.querySelector('.main-cart').dataset.id,
        selector: '#cart-dynamic-offers'
      },
      {
        id: document.querySelector('.main-cart').dataset.id,
        selector: '#cart-footer'
      },
      {
        id: document.querySelector('.main-cart').dataset.id,
        selector: '#cart-additionals'
      },
      {
        id: document.querySelector('.main-cart').dataset.id,
        selector: '#cart-static-offers-bar'
      }
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  async renderAndShowItems(updateOnly = false) {
    try {
      const sections = this.getSectionsToRender().map((section) => section.id).join(',')
      const response = await fetch(`?sections=${sections}`)

      const json = await response.json();
      if (json.errors) {
        throw json.errors;
      }

      this.getSectionsToRender().forEach((section) => {
        const sectionId = `shopify-section-${section.id}`;
        const elementToReplace = document.getElementById(sectionId).querySelector(section.selector) || document.getElementById(section.id);
        if (!elementToReplace) return;

        elementToReplace.innerHTML = this.getSectionInnerHTML(json[section.id], section.selector);
      });

      if (!updateOnly) {
        const parentDrawer = this.closest('[data-drawer]');
        if (!parentDrawer) return;
        Woolman.ModalsAndDrawers.showModalOrDrawer(parentDrawer.getAttribute('id'))
      }

    } catch(errors) {
      console.error(errors)
    }
  }

  async updateQuantities(line, quantity, name, element) {
    try {
      this.enableLoading(line);
      let productsToRemove = null;

      // If it is a bundle item, update the quantities of all child items
      productsToRemove = Object.assign(
        ...element.getAttribute('data-items-keys').split(',').map((id) => ({ [`${id}`]: 0 }))
      );

      const giftWrappingKey = element.getAttribute('data-gift-wrapping-key') || '';

      // Due to issues with the cart/update.js API when passing a key-value object as updates parameter,
      // we submit an array of numbers to cart/update.js, with the size matching the number of line items with updated prices.
      const cart = await window.fetchCart();
      let updates = [];
      const itemKeys = element.getAttribute('data-items-keys').split(',');

      cart.items.forEach((item) => {
        if (itemKeys.includes(item.key) || item.key == giftWrappingKey) {
          updates.push(quantity);
        } else {
          updates.push(item.quantity);
        }
      });

      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          updates,
          sections: this.getSectionsToRender().map(section => section.id),
          sections_url: window.location.pathname,
        }),
      });

      const json = await response.json();
      const { sections, item_count, errors, items } = json;

      if (errors) throw errors;

      if (productsToRemove) {
        const bundleItems = [];

        Object.entries(productsToRemove).forEach(([key, value]) => {
          const bundleLineItemData = document.querySelector(`cart-items [id="LineItemData-${key}"]`);
          const childProductDetails = JSON.parse(bundleLineItemData.textContent);
          const bundleTitle = childProductDetails.bundle_title;
          const bundleHandle = childProductDetails.bundle_handle;
          const price = childProductDetails.product.price;

          bundleItems.push({
            product: childProductDetails,
            name,
            quantity: value,
            price,
            bundled: true,
            bundleProduct: { title: bundleTitle, handle: bundleHandle },
          });
        });

        bundleItems.forEach(item => document.dispatchEvent(new CustomEvent('datalayer:cartUpdate', { detail: item })));
      }

      this.renderAndShowItems(true);

      document.dispatchEvent(new CustomEvent('cart:update', { detail: { json: json }}));

      this.updateLiveRegions(line, item_count);

      const lineItem = document.getElementById(`CartItem-${line}`);
      if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
        lineItem.querySelector(`[name="${name}"]`).focus();
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.disableLoading();
    }
  }

  async updateQuantity(line, quantity, name) {
    this.enableLoading(line);

    try {
      const response = await fetch(`${routes.cart_change_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: `application/json`,
        },
        body: JSON.stringify({
          line,
          quantity,
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });

      const json = await response.json();

      const { items, item_count, errors, attributes } = json;

      if (attributes._preorder === 'true') {
        const product_ids = items.map(item => `${item.product_id}`)
        const last_preorder_ids = attributes._preorder_ids?.split(',').filter(Boolean) || []
        const current_preorder_ids = last_preorder_ids
          .filter(preorder_id => product_ids.indexOf(preorder_id) >= 0)

        if (last_preorder_ids.length !== current_preorder_ids.length) {
          await fetch(`${routes.cart_update_url}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: `application/json`,
            },
            body: JSON.stringify({
              attributes: {
                _preorder: current_preorder_ids.length > 0 || '',
                _preorder_ids: current_preorder_ids.join(',')
              }
            })
          });
        }
      }

      if (errors) throw errors;

      const lineItem = document.getElementById(`CartItem-${line}`);
      const productDetails = JSON.parse(lineItem.querySelector('[data-product-json]').textContent);

      document.dispatchEvent(new CustomEvent('datalayer:cartUpdate', {
        detail: {
          product: productDetails,
          name,
          quantity,
          price: items?.[line - 1]?.price ?? productDetails.product.price,
          affiliatePartner: window.localStorage.getItem('affiliatePartner'),
          affiliateId: window.localStorage.getItem('affiliateId'),
          ttp: window.getCookieValue('_ttp'),
          ttclid: window.getCookieValue('ttclid'),
          eventId: crypto.randomUUID()
        },
      }));

      this.renderAndShowItems(true);

      document.dispatchEvent(new CustomEvent('cart:update', { detail: { json: json }}));

      this.updateLiveRegions(line, item_count);

      if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
        lineItem.querySelector(`[name="${name}"]`).focus();
      }
    } catch (errors) {
      console.error(errors);
    } finally {
      this.disableLoading();
    }
  }

  updateLiveRegions(line, itemCount) {
    if (this.currentItemCount === itemCount) {
      //document.getElementById(`Line-item-error-${line}`).querySelector('.cart-item__error-text').innerHTML = window.cartStrings.quantityError.replace('[quantity]', document.getElementById(`Quantity-${line}`).value);
    }

    this.currentItemCount = itemCount;
    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  enableLoading(line) {
    this.classList.add('is-processing');
    this.querySelectorAll(`#CartItem-${line} .loading-overlay`).forEach((overlay) => overlay.classList.remove('hidden'));
    document.activeElement.blur();
  }

  disableLoading() {
    this.classList.remove('is-processing');
    this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
  }
}
customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('keyup', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${routes.cart_update_url}`, {
          method: 'POST',
          headers: {
           'Content-Type': 'application/json',
            Accept: `application/json`,
          },
          ...{ body }
        });
      }, 300))
    }
  });
};

if (!customElements.get('cart-upsell-offer')) {
  customElements.define('cart-upsell-offer',class CartUpsellOffer extends HTMLElement {
    constructor() {
      super();
      const btn = this.querySelector('.cartUpsellOfferBtn');
      btn.addEventListener('click', this.addToCart.bind(this))
      this.productVariantId = this.dataset.productVariantId
      this.shouldRemove = this.dataset.shouldRemove
      if (this.shouldRemove === 'true') {
        this.removeFromCart()
      }
      // allow to submit only once
      this.isSubmitted = false

      window.fetchCart().then(cart => {
        if (cart.total_price === 0 && cart.items.length === 1 && cart.items[0].properties._upsell_offer === true) {
          this.removeFromCart();
        }
      })
    }

    async removeFromCart () {
      const response = await fetch(`${window.Shopify.routes.root}cart/clear.js`, {
        method: 'POST',
      })
      const json = await response.json()

      document.dispatchEvent(new CustomEvent('ajaxProduct:cleared',{
        detail: {
          json: json
        }
      }));
    }

    async addToCart (e) {
      e.preventDefault()

      if (this.isSubmitted) {
        console.log('cart-upsell-offer isLoading')
        return
      }

      this.isSubmitted = true
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          'items': [{
            'id': this.productVariantId,
            'quantity': 1,
            properties: {
              '_upsell_offer': true
            }
          }]
        }),
      });
      const json = await response.json();
      console.log('cart-upsell-offer json', { json })

      document.dispatchEvent(new CustomEvent('ajaxProduct:added',{
        detail: {
          json: json
        }
      }));
    }
  })
}

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.loadRecommendations();
      document.addEventListener('ajaxProduct:added', this.loadRecommendations.bind(this))
    }

    async loadRecommendations() {
      if (!this.dataset.productId || this.dataset.productId === '') return;

      try {
        const url = new URL(location.origin + routes.product_recommendations_url + '.json')
        url.searchParams.set('product_id', this.dataset.productId)
        url.searchParams.set('limit', this.dataset.limit || 10)
        url.searchParams.set('intent', this.dataset.intent || 'related')

        const response = await fetch(url)
        const { products } = await response.json();

        if (!products || products.length == 0) {
          throw 'No product recommendations found for products in the cart'
        }

        this.productsContainer = this.querySelector('[data-products-ajax]')
        this.productsContainer.innerHTML = '';

        let name = "cart-recommendations"
        try {
          name = this.productsContainer.closest("details").querySelector(".summary__title").textContent.trim();
        } catch (e) {}


        products.forEach((product, index) => {

          const template = this.querySelector('template').content.firstElementChild.cloneNode(true);
          const filename = product.featured_image.split('/').pop();
          const filedata = filename.split('.')
          const new_filename = `${filedata[0]}_150x150.${filedata[1]}`
          const url = product.featured_image.replace(filename, new_filename)

          template.querySelector('img').setAttribute('src', url)
          template.setAttribute('href', location.origin + product.url)
          template.setAttribute('title', product.title)

          this.productsContainer.appendChild(template)

          template.addEventListener('click', () => {
            document.dispatchEvent(new CustomEvent("snowplowProductListClick", {
              detail: {
                name,
                product: {
                  id: product.variants[0].sku,
                  name: product.title,
                  brand: product.vendor,
                  price: product.price,
                  category: product.type,
                  position: index,
                },
              }
            }));
          })
        })

        const intersectionCallback = (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const name = this.productsContainer.closest("details").querySelector(".summary__title").textContent.trim();
              const snowplowProductListViewEvent = {
                detail: {
                  name,
                  products: products.map((product, index) => ({
                    id: product.variants[0].sku,
                    name: product.title,
                    brand: product.vendor,
                    price: product.price,
                    category: product.type,
                    position: index,
                  }))
                }
              };

              const event = new CustomEvent("snowplowProductListView", snowplowProductListViewEvent);
              document.dispatchEvent(event);
              intersection.unobserve(entry.target);
            }
          });
        }

        const intersection = new IntersectionObserver(intersectionCallback);
        intersection.observe(this.productsContainer, { threshold: 0.2 });
      } catch(error) {
        this.setAttribute('hidden', true)
        console.warn(error)
      }
    }
  })
}
